// ============================================================================
// NotesStore - Manages Client Notes
// ============================================================================
// Handles notes CRUD, filtering, caching, and realtime subscriptions.
// Separated from ClientStore for cleaner architecture.
//
// Events: 'notes:loaded', 'notes:added', 'notes:updated', 'notes:deleted'
//
// Usage:
//   const notes = await state.notes.getClientNotes(clientId);
//   await state.notes.addNote(clientId, { content: '...' });
// ============================================================================

import { supabase, auth } from 'app/auth/auth.js';
import { EventEmitter } from './event-emitter.js';
import { CacheStore } from './cache-store.js';
import { globalSubscriptionManager } from './subscription-manager.js';

const MAX_NOTES_PER_CLIENT = 100;
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

class NotesStore extends EventEmitter {
    constructor() {
        super();
        this.cache = new CacheStore(50, CACHE_TTL); // Cache notes for 50 clients
        this.subscriptions = globalSubscriptionManager;
        this.activeSubscriptions = new Map();
        // Tie each realtime channel to the cache entry it backs: when a client's
        // notes fall out of the LRU, drop its subscription too, so channels can
        // never outlive their cached data and accumulate for the whole session.
        this.cache.onEvict = (key) => {
            if (typeof key === 'string' && key.startsWith('notes:')) {
                this.removeSubscription(key.slice('notes:'.length));
            }
        };
    }

    // ========================================================================
    // Notes Fetching & Filtering
    // ========================================================================

    async loadNotes(clientId) {
        try {
            const { data, error } = await supabase
                .from('notes')
                .select(`
                    *,
                    note_services(service_id),
                    author:profiles!author_id (
                        user_id,
                        email,
                        full_name,
                        short_name
                    ),
                    editor:profiles!last_edited_by (
                        user_id,
                        full_name,
                        short_name
                    )
                `)
                .eq('client_id', clientId)
                .eq('is_deleted', false)
                .order('created_at', { ascending: false })
                .limit(MAX_NOTES_PER_CLIENT);

            if (error) throw error;

            // Normalize note_services join into flat service_ids array for downstream code
            if (data) {
                data.forEach(note => {
                    note.service_ids = (note.note_services || []).map(ns => ns.service_id);
                    delete note.note_services;
                });
            }

            const notes = data || [];

            // Fetch attachments for all loaded notes in a single query
            if (notes.length > 0) {
                const noteIds = notes.map(n => n.id);
                const { data: attachments, error: attError } = await supabase
                    .from('note_attachments')
                    .select('*')
                    .in('note_id', noteIds);

                if (attError) {
                    console.error('[NotesStore] Failed to load attachments:', attError.message);
                }

                // Group attachments by note_id and attach to each note
                const attachmentsByNote = new Map();
                (attachments || []).forEach(att => {
                    if (!attachmentsByNote.has(att.note_id)) {
                        attachmentsByNote.set(att.note_id, []);
                    }
                    attachmentsByNote.get(att.note_id).push(att);
                });

                notes.forEach(note => {
                    note.attachments = attachmentsByNote.get(note.id) || [];
                });
            }

            return notes;
        } catch (error) {
            console.error('[NotesStore] Load failed:', error.message);
            return [];
        }
    }

    async getClientNotes(clientId, filters = {}) {
        if (!clientId) return [];

        const { forceRefresh = false, ...filterOptions } = filters;
        const cacheKey = `notes:${clientId}`;

        let notes = this.cache.get(cacheKey);

        if (forceRefresh || !notes) {
            notes = await this.loadNotes(clientId);
            this.cache.set(cacheKey, notes);
            this._ensureSubscription(clientId);
            this.emit('notes:loaded', { clientId, notes });
        } else {
            this._ensureSubscription(clientId);
        }

        return this._applyFilters(notes || [], filterOptions);
    }

    _applyFilters(notes, filters = {}) {
        if (!Array.isArray(notes) || notes.length === 0) return [];

        const searchTerm = filters.search?.toLowerCase();
        const hasCategories = filters.categories?.length > 0;
        const hasServiceIds = filters.serviceIds?.length > 0;
        const hasMeetingIds = filters.meetingIds?.length > 0;
        const startTime = filters.startDate ? new Date(filters.startDate).getTime() : null;
        const endTime = filters.endDate ? new Date(filters.endDate).getTime() : null;

        if (!searchTerm && !hasCategories && !hasServiceIds && !hasMeetingIds && !startTime && !endTime) {
            return [...notes];
        }

        return notes.filter(note => {
            if (searchTerm && !note.content?.toLowerCase().includes(searchTerm)) {
                return false;
            }
            if (hasCategories) {
                const noteCategories = note.categories || [];
                if (!filters.categories.some(cat => noteCategories.includes(cat))) {
                    return false;
                }
            }
            if (hasServiceIds) {
                const noteServiceIds = note.service_ids || [];
                if (!filters.serviceIds.some(id => noteServiceIds.includes(id))) {
                    return false;
                }
            }
            if (hasMeetingIds) {
                if (!note.meeting_id || !filters.meetingIds.includes(note.meeting_id)) {
                    return false;
                }
            }
            if (startTime || endTime) {
                const noteTime = new Date(note.created_at).getTime();
                if (startTime && noteTime < startTime) return false;
                if (endTime && noteTime > endTime) return false;
            }
            return true;
        });
    }

    // ========================================================================
    // Notes CRUD
    // ========================================================================

    async addNote(clientId, noteData) {
        try {
            const user = auth.user;
            const serviceIds = noteData.serviceIds || [];
            const files = noteData.files || [];

            const note = {
                client_id: clientId,
                author_id: user?.id || 'unknown',
                categories: noteData.categories?.length ? noteData.categories : ['General'],
                content: noteData.content,
                meeting_id: noteData.meetingId || null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                is_deleted: false
            };

            const { data, error } = await supabase
                .from('notes')
                .insert(note)
                .select()
                .single();

            if (error) throw error;

            // Insert service links into junction table
            if (serviceIds.length > 0) {
                const rows = serviceIds.map(sid => ({ note_id: data.id, service_id: sid }));
                const { error: junctionError } = await supabase.from('note_services').insert(rows);
                if (junctionError) console.error('[NotesStore] Failed to insert note_services:', junctionError.message);
            }

            // Upload file attachments
            const attachments = [];
            if (files.length > 0) {
                attachments.push(...await this._uploadFiles(clientId, data.id, files, user?.id));
            }

            // Attach service_ids and attachments to returned data for cache consistency
            data.service_ids = serviceIds;
            data.attachments = attachments;

            this._updateCache(clientId, notes => [data, ...notes]);
            this._ensureSubscription(clientId);
            this.emit('notes:added', { clientId, note: data });

            return { data, error: null };
        } catch (error) {
            console.error('[NotesStore] Add failed:', error.message);
            return { data: null, error };
        }
    }

    async updateNote(noteId, updates) {
        try {
            // Extract service_ids and attachment operations from updates
            const serviceIds = updates.service_ids;
            const newFiles = updates.newFiles || [];
            const removeAttachmentIds = updates.removeAttachmentIds || [];
            const { service_ids: _removed, newFiles: _nf, removeAttachmentIds: _ra, ...notePayload } = updates;

            const payload = {
                ...notePayload,
                updated_at: new Date().toISOString()
            };

            const { data, error } = await supabase
                .from('notes')
                .update(payload)
                .eq('id', noteId)
                .select()
                .single();

            if (error) throw error;

            // Update junction table if service_ids were provided
            if (serviceIds !== undefined) {
                // Delete existing links and re-insert
                await supabase.from('note_services').delete().eq('note_id', noteId);
                if (serviceIds.length > 0) {
                    const rows = serviceIds.map(sid => ({ note_id: noteId, service_id: sid }));
                    const { error: junctionError } = await supabase.from('note_services').insert(rows);
                    if (junctionError) console.error('[NotesStore] Failed to update note_services:', junctionError.message);
                }
            }

            const clientId = data.client_id;
            const user = auth.user;

            // Remove attachments (delete from storage + DB)
            if (removeAttachmentIds.length > 0) {
                // Fetch the file_paths for the attachments being removed
                const { data: toRemove } = await supabase
                    .from('note_attachments')
                    .select('id, file_path')
                    .in('id', removeAttachmentIds);

                if (toRemove && toRemove.length > 0) {
                    const filePaths = toRemove.map(a => a.file_path);
                    await supabase.storage.from('note-attachments').remove(filePaths);
                    await supabase.from('note_attachments').delete().in('id', removeAttachmentIds);
                }
            }

            // Upload new files
            if (newFiles.length > 0) {
                await this._uploadFiles(clientId, noteId, newFiles, user?.id);
            }

            // Fetch remaining attachments for this note to get the accurate list
            const { data: currentAttachments } = await supabase
                .from('note_attachments')
                .select('*')
                .eq('note_id', noteId);

            // Attach service_ids and attachments to returned data for cache consistency
            data.service_ids = serviceIds !== undefined ? serviceIds : [];
            data.attachments = currentAttachments || [];

            this._updateCache(clientId, notes => {
                const idx = notes.findIndex(n => n.id === noteId);
                if (idx === -1) return notes;
                const updated = [...notes];
                updated[idx] = data;
                return updated;
            });

            this.emit('notes:updated', { clientId, note: data });
            return { data, error: null };
        } catch (error) {
            console.error('[NotesStore] Update failed:', error.message);
            return { data: null, error };
        }
    }

    async deleteNote(noteId) {
        try {
            const user = auth.user;

            const { data, error } = await supabase
                .from('notes')
                .update({
                    is_deleted: true,
                    deleted_by: user?.id || 'unknown',
                    deleted_at: new Date().toISOString()
                })
                .eq('id', noteId)
                .select()
                .single();

            if (error) throw error;

            const clientId = data.client_id;
            this._updateCache(clientId, notes => notes.filter(n => n.id !== noteId));
            this.emit('notes:deleted', { clientId, noteId });

            return { data, error: null };
        } catch (error) {
            console.error('[NotesStore] Delete failed:', error.message);
            return { data: null, error };
        }
    }

    // ========================================================================
    // File Attachments
    // ========================================================================

    /**
     * Upload files to storage and insert attachment records.
     * @param {string} clientId - Client ID for storage path
     * @param {string} noteId - Note ID for storage path and FK
     * @param {File[]} files - Array of File objects to upload
     * @param {string} userId - Uploading user's ID
     * @returns {Object[]} Array of created attachment records
     */
    async _uploadFiles(clientId, noteId, files, userId) {
        const attachments = [];

        for (const file of files) {
            const filePath = `${clientId}/${noteId}/${file.name}`;

            const { error: uploadError } = await supabase.storage
                .from('note-attachments')
                .upload(filePath, file);

            if (uploadError) {
                console.error('[NotesStore] File upload failed:', file.name, uploadError.message);
                continue;
            }

            const record = {
                note_id: noteId,
                file_name: file.name,
                file_path: filePath,
                mime_type: file.type || null,
                size_bytes: file.size,
                uploaded_by: userId || null
            };

            const { data: attData, error: attError } = await supabase
                .from('note_attachments')
                .insert(record)
                .select()
                .single();

            if (attError) {
                console.error('[NotesStore] Attachment record insert failed:', attError.message);
                // Clean up the uploaded file since the record failed
                await supabase.storage.from('note-attachments').remove([filePath]);
                continue;
            }

            attachments.push(attData);
        }

        return attachments;
    }

    /**
     * Get a signed URL for downloading an attachment.
     * @param {string} filePath - Storage path of the file
     * @returns {string|null} Signed URL or null on error
     */
    async getAttachmentUrl(filePath) {
        const { data, error } = await supabase.storage
            .from('note-attachments')
            .createSignedUrl(filePath, 3600); // 1 hour expiry

        if (error) {
            console.error('[NotesStore] Failed to create signed URL:', error.message);
            return null;
        }

        return data?.signedUrl || null;
    }

    // ========================================================================
    // Realtime Subscriptions
    // ========================================================================

    _ensureSubscription(clientId) {
        if (!clientId) return;
        // Key by string so it matches the cache key the onEvict hook derives
        // (cache keys are the stringified `notes:${clientId}`).
        const key = String(clientId);
        if (this.activeSubscriptions.has(key)) return;

        const unsubscribe = this.subscriptions.subscribe(
            'notes',
            `client_id=eq.${clientId}`,
            (payload) => this._handleChange(payload)
        );

        this.activeSubscriptions.set(key, unsubscribe);
    }

    removeSubscription(clientId) {
        const key = String(clientId);
        const unsubscribe = this.activeSubscriptions.get(key);
        if (unsubscribe) {
            unsubscribe();
            this.activeSubscriptions.delete(key);
        }
    }

    _handleChange(payload) {
        const clientId = payload.new?.client_id ?? payload.old?.client_id;
        if (!clientId) return;

        switch (payload.eventType) {
            case 'INSERT': {
                // Invalidate cache so next fetch includes note_services join data
                this.cache.delete(`notes:${clientId}`);
                this.emit('notes:added', { clientId, note: payload.new });
                break;
            }
            case 'UPDATE': {
                const note = payload.new;
                if (note.is_deleted) {
                    const updated = this._updateCache(clientId, notes =>
                        notes.filter(n => n.id !== note.id)
                    );
                    if (updated) this.emit('notes:deleted', { clientId, noteId: note.id });
                } else {
                    // Invalidate cache so next fetch includes note_services join data
                    this.cache.delete(`notes:${clientId}`);
                    this.emit('notes:updated', { clientId, note });
                }
                break;
            }
            case 'DELETE': {
                const note = payload.old;
                const updated = this._updateCache(clientId, notes =>
                    notes.filter(n => n.id !== note.id)
                );
                if (updated) this.emit('notes:deleted', { clientId, noteId: note.id });
                break;
            }
        }
    }

    // ========================================================================
    // Cache Management
    // ========================================================================

    _updateCache(clientId, updater) {
        const cacheKey = `notes:${clientId}`;
        const notes = this.cache.get(cacheKey);
        if (!notes) return null;

        const updated = updater([...notes]);
        if (!updated) return null;

        this.cache.set(cacheKey, updated.slice(0, MAX_NOTES_PER_CLIENT));
        return updated;
    }

    invalidate(clientId) {
        this.cache.delete(`notes:${clientId}`);
        this.removeSubscription(clientId);
    }

    // ========================================================================
    // Cleanup
    // ========================================================================

    destroy() {
        this.activeSubscriptions.forEach(fn => fn());
        this.activeSubscriptions.clear();
        this.cache.clear();
        super.destroy();
    }
}

export { NotesStore };
